<template>
  <div class="container mt-5">
    <h1 class="text-center mb-4">{{ $t("signup.role_selection.title") }}</h1>
    <p class="text-center text-muted mb-5">
      {{ $t("signup.role_selection.subtitle") }}
    </p>

    <div class="row justify-content-center g-4 mb-4">
      <div class="col-md-6">
        <div class="card h-100 role-card" @click="selectRole('corporate')">
          <div class="card-body p-4">
            <div class="text-center mb-3">
              <font-awesome-icon
                icon="building"
                size="3x"
                class="text-primary"
              />
            </div>
            <h3 class="text-center mb-3">
              {{ $t("signup.role_selection.corporate_title") }}
            </h3>
            <p class="text-muted mb-3 text-center">
              <strong>{{
                $t("signup.role_selection.corporate_perfect_if")
              }}</strong>
            </p>
            <ul class="list-unstyled small">
              <li class="mb-2">
                <font-awesome-icon
                  icon="check"
                  class="text-primary me-2"
                  fixed-width
                />
                {{ $t("signup.role_selection.corporate_benefit1") }}
              </li>
              <li class="mb-2">
                <font-awesome-icon
                  icon="check"
                  class="text-primary me-2"
                  fixed-width
                />
                {{ $t("signup.role_selection.corporate_benefit2") }}
              </li>
              <li class="mb-2">
                <font-awesome-icon
                  icon="check"
                  class="text-primary me-2"
                  fixed-width
                />
                {{ $t("signup.role_selection.corporate_benefit3") }}
              </li>
              <li class="mb-2">
                <font-awesome-icon
                  icon="check"
                  class="text-primary me-2"
                  fixed-width
                />
                {{ $t("signup.role_selection.corporate_benefit4") }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card h-100 role-card" @click="selectRole('producer')">
          <div class="card-body p-4">
            <div class="text-center mb-3">
              <font-awesome-icon
                icon="solar-panel"
                size="3x"
                class="text-success"
              />
            </div>
            <h3 class="text-center mb-3">
              {{ $t("signup.role_selection.producer_title") }}
            </h3>
            <p class="text-muted mb-3 text-center">
              <strong>{{
                $t("signup.role_selection.producer_perfect_if")
              }}</strong>
            </p>
            <ul class="list-unstyled small">
              <li class="mb-2">
                <font-awesome-icon
                  icon="check"
                  class="text-success me-2"
                  fixed-width
                />
                {{ $t("signup.role_selection.producer_benefit1") }}
              </li>
              <li class="mb-2">
                <font-awesome-icon
                  icon="check"
                  class="text-success me-2"
                  fixed-width
                />
                {{ $t("signup.role_selection.producer_benefit2") }}
              </li>
              <li class="mb-2">
                <font-awesome-icon
                  icon="check"
                  class="text-success me-2"
                  fixed-width
                />
                {{ $t("signup.role_selection.producer_benefit3") }}
              </li>
              <li class="mb-2">
                <font-awesome-icon
                  icon="check"
                  class="text-success me-2"
                  fixed-width
                />
                {{ $t("signup.role_selection.producer_benefit4") }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="row justify-content-center">
      <div class="col-md-10 col-lg-8">
        <div class="card border-warning">
          <div class="card-body p-4">
            <h5 class="text-center mb-3">
              <font-awesome-icon
                icon="question-circle"
                class="text-warning me-2"
              />
              {{ $t("signup.role_selection.help_title") }}
            </h5>
            <p class="text-center text-muted small mb-3">
              {{ $t("signup.role_selection.help_description") }}
            </p>

            <div v-if="!showHelpForm" class="text-center">
              <button class="btn btn-warning" @click="showHelpForm = true">
                {{ $t("signup.role_selection.help_button") }}
              </button>
            </div>

            <div v-else>
              <div class="mb-3">
                <label for="helpMessage" class="form-label">{{
                  $t("signup.role_selection.help_form_label")
                }}</label>
                <textarea
                  id="helpMessage"
                  v-model="helpMessage"
                  class="form-control"
                  rows="4"
                  :maxlength="helpMessageMaxLength"
                  :placeholder="
                    $t('signup.role_selection.help_form_placeholder')
                  "
                ></textarea>
              </div>
              <div class="d-flex gap-2 justify-content-end">
                <button
                  class="btn btn-outline-secondary"
                  @click="cancelHelp"
                  :disabled="isSending"
                >
                  {{ $t("common.cancel") }}
                </button>
                <button
                  class="btn btn-warning"
                  @click="submitHelpRequest"
                  :disabled="!helpMessage.trim() || isSending || isRateLimited"
                >
                  <span
                    v-if="isSending"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  {{ $t("signup.role_selection.help_submit") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isAxiosError } from "axios";
import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  ROLE_HELP_MESSAGE_PREFIX,
  SIGNUP_MESSAGE_MAX_LENGTH,
} from "@/signup/deliverNoteLimits";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const router = useRouter();
const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler, globalWarningHandler } =
  useGlobalHandlers();

const showHelpForm = ref(false);
const helpMessage = ref("");
const isSending = ref(false);
// Set on a 429 so the retained message cannot be resubmitted until the form is reset.
const isRateLimited = ref(false);
// The backend limit applies to the prefixed message, so the textarea gets what is left.
const helpMessageMaxLength =
  SIGNUP_MESSAGE_MAX_LENGTH - ROLE_HELP_MESSAGE_PREFIX.length;

const selectRole = (role: "producer" | "corporate") => {
  if (role === "producer") {
    router.push("/signup/producer");
  } else {
    router.push("/signup/corporate");
  }
};

const cancelHelp = () => {
  showHelpForm.value = false;
  helpMessage.value = "";
  isRateLimited.value = false;
};

const submitHelpRequest = async () => {
  if (!helpMessage.value.trim()) {
    return;
  }

  isSending.value = true;

  try {
    await goSolidApi.post("/api/utils/signup/deliver-note", {
      message: `${ROLE_HELP_MESSAGE_PREFIX}${helpMessage.value}`,
      context: "role_selection",
    });

    globalSuccessHandler(
      t("common.success"),
      t("signup.role_selection.help_success")
    );
    cancelHelp();
  } catch (error) {
    // The backend caps signup messages per account (HTTP 429); that is guidance, not a failure.
    if (isAxiosError(error) && error.response?.status === 429) {
      isRateLimited.value = true;
      globalWarningHandler(
        t("signup.message_rate_limited_title"),
        t("signup.message_rate_limited")
      );
    } else {
      globalErrorHandler(error);
    }
  } finally {
    isSending.value = false;
  }
};
</script>

<style scoped lang="scss">
.role-card {
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    border-color: var(--phoenix-primary);
  }
}
</style>
